import { CookieKey, ParcelSearchesSource } from '@/constants';
import { useAuth, useGeo, useGlobalState } from '@/hooks';
import { useRecaptchaContext } from '@/modules/pmt/context';
import { checkPmtQueryParamsValid, recordSearchT1 } from '@/modules/pmt/utils';
import { getPmtLangHref, gtmClickEmailCta, gtmParcelSearches, trackingParamExtractionModify } from '@/utils';
import { useStatsigClient } from '@statsig/react-bindings';
import Cookies from 'js-cookie';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { ReCaptchaV3Action, pmtQueryParams } from '../constants';
import { useFetchShipments } from './useFetchShipments';

export const useSearchShipments = () => {
  const router = useRouter();
  const { pmtCurrentLanguageCode } = useGlobalState();
  const { fetchShipments } = useFetchShipments();
  const { isV3Loaded, checkWithReCaptchaV3 } = useRecaptchaContext();
  const { authGuest } = useAuth();
  const { geoInfo } = useGeo();
  const { logEvent } = useStatsigClient();

  useEffect(() => {
    (async () => {
      if (!isV3Loaded) return;
      const { shipmentId, pParcelIds, pParcelId } = router.query;

      if (!pParcelIds && !shipmentId && !pParcelId) return;

      if (!geoInfo.fetched) return;

      const accessToken = Cookies.get(CookieKey.AccessToken);

      switch (true) {
        // Tracking from email
        case checkPmtQueryParamsValid(router.query, 'shipmentId', shipmentId): {
          if (!accessToken) {
            await authGuest();
          }
          await checkWithReCaptchaV3({
            action: ReCaptchaV3Action.TrackingFromEmail,
            callback: () => {
              fetchShipments([shipmentId as string]);
              gtmClickEmailCta();
            },
          });

          break;
        }

        // Tracking from AMP
        case checkPmtQueryParamsValid(router.query, 'pParcelIds', pParcelIds): {
          if (!accessToken) {
            await authGuest();
          }
          await checkWithReCaptchaV3({
            action: ReCaptchaV3Action.TrackingFromAMP,
            callback: () => {
              const parcelIds = trackingParamExtractionModify(pParcelIds as string);
              fetchShipments(parcelIds);
              recordSearchT1(parcelIds);
              gtmParcelSearches(parcelIds.length, ParcelSearchesSource.AMP);
              logEvent('parcel_searches', parcelIds.length, { source: ParcelSearchesSource.AMP });
              void router.push(getPmtLangHref(pmtCurrentLanguageCode), undefined, { shallow: true });
            },
          });

          break;
        }

        // Tracking from PMC
        case checkPmtQueryParamsValid(router.query, 'pParcelId', pParcelId): {
          if (!accessToken) {
            await authGuest();
          }
          const carrierSlugParam = router.query[pmtQueryParams.CarrierSlug];
          const carrierSlug = typeof carrierSlugParam === 'string' ? carrierSlugParam : undefined;
          await checkWithReCaptchaV3({
            action: ReCaptchaV3Action.TrackingFromPMC,
            callback: () => {
              const parcelIds = trackingParamExtractionModify(pParcelId as string);
              fetchShipments(parcelIds, { carrierSlug });
              recordSearchT1(parcelIds);
              // PMC only allow to search for 1 shipment
              gtmParcelSearches(1, ParcelSearchesSource.PMC);
              logEvent('parcel_searches', 1, { source: ParcelSearchesSource.PMC });
              void router.push(getPmtLangHref(pmtCurrentLanguageCode), undefined, { shallow: true });
            },
          });
          break;
        }

        default:
          break;
      }
    })();
  }, [fetchShipments, router.push, router.query, pmtCurrentLanguageCode, isV3Loaded, geoInfo.fetched]);
};
